import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImagePlus, Trash2 } from "lucide-react";

const MAX_SIDE = 400;

export const CompanyLogoCard = () => {
  const { t } = useTranslation("app");
  const [logo, setLogo] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/company/logo").then(({ data }) => {
      setLogo(data.logo || "");
      setName(data.name || "");
    }).catch(() => setLogo(""));
  }, []);

  if (logo === null) return null;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("set.logoBadFile")); return; }
    const img = new Image();
    img.onload = async () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const isPng = file.type.includes("png") || file.type.includes("webp");
      const dataUrl = isPng ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.85);
      setBusy(true);
      try {
        const { data } = await api.put("/settings/logo", { logo: dataUrl });
        setLogo(data.logo);
        toast.success(t("set.logoSaved"));
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("set.saveErr"));
      } finally { setBusy(false); }
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => toast.error(t("set.logoBadFile"));
    img.src = URL.createObjectURL(file);
  };

  const removeLogo = async () => {
    setBusy(true);
    try {
      await api.put("/settings/logo", { logo: "" });
      setLogo("");
      toast.success(t("set.logoRemoved"));
    } catch { toast.error(t("set.saveErr")); }
    finally { setBusy(false); }
  };

  const saveName = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/company-name", { name });
      setName(data.name);
      toast.success(t("set.saved"));
    } catch { toast.error(t("set.saveErr")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-3" data-testid="company-logo-card">
      <div>
        <div className="font-heading font-bold tracking-tight">{t("set.logoTitle")}</div>
        <p className="text-sm text-slate-600 mt-1">{t("set.logoSub")}</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
            {t("set.companyNameLabel")}
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("set.companyNamePh")}
            maxLength={60}
            data-testid="company-name-input"
          />
        </div>
        <Button size="sm" onClick={saveName} disabled={busy} data-testid="company-name-save-btn">
          {t("set.tipsSave")}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="h-20 w-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
          {logo
            ? <img src={logo} alt="logo" className="max-h-full max-w-full object-contain" data-testid="company-logo-preview" />
            : <span className="text-xs text-slate-400">{t("set.logoEmpty")}</span>}
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="logo-upload-btn">
            <ImagePlus className="h-4 w-4 mr-1" /> {logo ? t("set.logoChange") : t("set.logoUpload")}
          </Button>
          {logo && (
            <Button size="sm" variant="outline" className="text-red-600" onClick={removeLogo} disabled={busy} data-testid="logo-remove-btn">
              <Trash2 className="h-4 w-4 mr-1" /> {t("set.logoRemove")}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
